import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '../../mui-styles-bridge.js';
import { Grid, TextField } from '@mui/material';

const styles = theme => ({
  root: {
    width: '100%',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
  },
});

const getDefaultAddress = () => ({
  formatted: '',
  street_address: '',
  locality: '',
  region: '',
  postal_code: '',
  country: '',
});

const DEFAULT_ADDRESS_FIELD_CONFIG = {
  line1: { show: true, required: true },
  line2: { show: true, required: false },
  locality: { show: true, required: true },
  region: { show: true, required: true },
  postalCode: { show: true, required: true },
  country: { show: true, required: true },
};

const normalizeAddressFieldConfig = addressFieldConfig => {
  const normalized = {
    ...DEFAULT_ADDRESS_FIELD_CONFIG,
    ...(addressFieldConfig || {}),
  };

  Object.keys(normalized).forEach(key => {
    const merged = {
      ...(DEFAULT_ADDRESS_FIELD_CONFIG[key] || { show: true, required: false }),
      ...(normalized[key] || {}),
    };
    if (!merged.show) {
      merged.required = false;
    }
    normalized[key] = merged;
  });

  return normalized;
};

const buildFormattedAddress = ({ line1, line2, locality, region, postalCode, country }, fieldConfig) => {
  const lines = [];
  if (fieldConfig.line1.show && line1) lines.push(line1);
  if (fieldConfig.line2.show && line2) lines.push(line2);

  const localityParts = [];
  if (fieldConfig.locality.show && locality) localityParts.push(locality);
  if (fieldConfig.region.show && region) localityParts.push(region);
  const localityLine = localityParts.join(', ');
  const localityWithPostcode = [
    localityLine,
    fieldConfig.postalCode.show ? postalCode : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (localityWithPostcode) lines.push(localityWithPostcode);
  if (fieldConfig.country.show && country) lines.push(country);

  return lines.join('\n');
};

class Address extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      line1: '',
      line2: '',
      locality: '',
      region: '',
      postalCode: '',
      country: '',
    };
  }

  componentDidMount() {
    const formAnswer = this.props.form.answer_data[this.props.data.key];
    if (formAnswer && formAnswer.address_answer) {
      this.hydrateFromAddress(formAnswer.address_answer);
      return;
    }
    if (
      formAnswer &&
      Array.isArray(formAnswer.answer) &&
      formAnswer.answer[0] &&
      typeof formAnswer.answer[0] === 'object'
    ) {
      this.hydrateFromAddress(formAnswer.answer[0]);
      return;
    }

    const defaultData = this.props.data.data;
    if (defaultData && typeof defaultData === 'object' && !Array.isArray(defaultData)) {
      this.hydrateFromAddress(defaultData);
    }
  }

  hydrateFromAddress = address => {
    const safe = { ...getDefaultAddress(), ...(address || {}) };
    const formattedLines =
      typeof safe.formatted === 'string' && safe.formatted.length > 0
        ? safe.formatted.split('\n')
        : [];
    const line1FromFormatted = formattedLines[0] || '';
    const line2FromFormatted = formattedLines[1] || '';

    this.setState({
      line1: line1FromFormatted || safe.street_address || '',
      line2: line2FromFormatted || '',
      locality: safe.locality || '',
      region: safe.region || '',
      postalCode: safe.postal_code || '',
      country: safe.country || '',
    });
  };

  getAddressPayload = () => {
    const fieldConfig = normalizeAddressFieldConfig(this.props.data.addressFields ?? this.props.data.addressFieldConfig);
    const line1 = fieldConfig.line1.show ? this.state.line1.trim() : '';
    const line2 = fieldConfig.line2.show ? this.state.line2.trim() : '';
    const locality = fieldConfig.locality.show ? this.state.locality.trim() : '';
    const region = fieldConfig.region.show ? this.state.region.trim() : '';
    const postalCode = fieldConfig.postalCode.show ? this.state.postalCode.trim() : '';
    const country = fieldConfig.country.show ? this.state.country.trim() : '';
    const streetAddress = [line1, line2].filter(Boolean).join(' ');

    return {
      formatted: buildFormattedAddress(
        { line1, line2, locality, region, postalCode, country },
        fieldConfig
      ),
      street_address: streetAddress,
      locality,
      region,
      postal_code: postalCode,
      country,
    };
  };

  handleChange = field => event => {
    this.setState({ [field]: event.target.value });
  };

  handleBlur = () => {
    const payload = this.getAddressPayload();
    this.props.getDataFromComponent(
      { address_answer: payload },
      this.props.data.key,
      this.props.data.question_text,
      payload
    );
  };

  render() {
    const { classes, data } = this.props;
    const fieldConfig = normalizeAddressFieldConfig(data.addressFields ?? data.addressFieldConfig);
    const readOnly = Boolean(data.read_only);
    const formatted = this.getAddressPayload().formatted;

    return (
      <div className={classes.root}>
          <Grid container spacing={2}>
            {fieldConfig.line1.show && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Address line 1"
                  value={this.state.line1}
                  onChange={this.handleChange('line1')}
                  onBlur={this.handleBlur}
                  disabled={readOnly}
                  required={Boolean(fieldConfig.line1.required)}
                />
              </Grid>
            )}
            {fieldConfig.line2.show && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Address line 2"
                  value={this.state.line2}
                  onChange={this.handleChange('line2')}
                  onBlur={this.handleBlur}
                  disabled={readOnly}
                  required={Boolean(fieldConfig.line2.required)}
                />
              </Grid>
            )}
            {fieldConfig.locality.show && (
              <Grid item xs={12} sm={fieldConfig.region.show ? 6 : 12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="City / Locality"
                  value={this.state.locality}
                  onChange={this.handleChange('locality')}
                  onBlur={this.handleBlur}
                  disabled={readOnly}
                  required={Boolean(fieldConfig.locality.required)}
                />
              </Grid>
            )}
            {fieldConfig.region.show && (
              <Grid item xs={12} sm={fieldConfig.locality.show ? 6 : 12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="State / Region"
                  value={this.state.region}
                  onChange={this.handleChange('region')}
                  onBlur={this.handleBlur}
                  disabled={readOnly}
                  required={Boolean(fieldConfig.region.required)}
                />
              </Grid>
            )}
            {fieldConfig.postalCode.show && (
              <Grid item xs={12} sm={fieldConfig.country.show ? 6 : 12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Postal / ZIP code"
                  value={this.state.postalCode}
                  onChange={this.handleChange('postalCode')}
                  onBlur={this.handleBlur}
                  disabled={readOnly}
                  required={Boolean(fieldConfig.postalCode.required)}
                />
              </Grid>
            )}
            {fieldConfig.country.show && (
              <Grid item xs={12} sm={fieldConfig.postalCode.show ? 6 : 12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Country"
                  value={this.state.country}
                  onChange={this.handleChange('country')}
                  onBlur={this.handleBlur}
                  disabled={readOnly}
                  required={Boolean(fieldConfig.country.required)}
                />
              </Grid>
            )}
          </Grid>
      </div>
    );
  }
}

Address.propTypes = {
  classes: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  getDataFromComponent: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  form: state.form,
});

export default connect(mapStateToProps)(withStyles(styles)(Address));
